import React, { FunctionComponent } from 'react';

import { Dropdown, DropdownButton, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { DatePickerWithLocale } from 'core/datepicker/DatePicker';
import { ErrorDisplay } from 'social/publisher/error-display/ErrorDisplay';
import {
    GoogleAlertType,
    GoogleButtonOption,
    GoogleButtonType,
    GoogleFields,
    PublisherError,
} from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import { GOOGLE_BUTTON_OPTIONS } from 'social/publisher/util/Constants';

import 'social/publisher/google-fields-selector/GoogleFieldsSelector.scss';

interface Props {
    googleFields: GoogleFields;
    publisherErrors: PublisherError[];
}
type GoogleFieldsView = {
    isMakeAlertSelected: boolean;
    isMakeEventSelected: boolean;
    isAddButtonSelected: boolean;
    eventTitle: string;
    startDate: Date;
    endDate: Date;
    buttonType: GoogleButtonType;
    buttonUrl: string;
};
export const GoogleFieldsSelector: FunctionComponent<Props> = ({ googleFields, publisherErrors }) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation('social/publisher');
    const fromGoogleFieldsToView = (fieldsData: GoogleFields): GoogleFieldsView => {
        const today = new Date();
        const view: GoogleFieldsView = {
            isMakeAlertSelected: false,
            isMakeEventSelected: false,
            isAddButtonSelected: false,
            eventTitle: '',
            startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0),
            endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 30, 0),
            buttonType: GOOGLE_BUTTON_OPTIONS[0].value,
            buttonUrl: '',
        };

        if (fieldsData.alertType === GoogleAlertType.COVID_19) {
            view.isMakeAlertSelected = true;
        }
        if (fieldsData.event) {
            view.isMakeEventSelected = true;
            view.eventTitle = fieldsData.event.title;
            view.startDate = new Date(fieldsData.event.startDate);
            view.endDate = new Date(fieldsData.event.endDate);
        }
        if (fieldsData.callToAction) {
            view.isAddButtonSelected = true;
            view.buttonType = fieldsData.callToAction.buttonType;
            if (fieldsData.callToAction.buttonType === GoogleButtonType.CALL) {
                view.buttonUrl = '';
            } else {
                view.buttonUrl = fieldsData.callToAction.link as string;
            }
        }
        return view;
    };
    const fromViewToGoogleFields = (viewData: GoogleFieldsView): GoogleFields => {
        const fields: GoogleFields = {
            alertType: null,
            event: null,
            callToAction: null,
        };

        if (viewData.isMakeAlertSelected) {
            fields.alertType = GoogleAlertType.COVID_19;
        }
        if (viewData.isMakeEventSelected) {
            fields.event = {
                title: viewData.eventTitle,
                startDate: viewData.startDate.getTime(),
                endDate: viewData.endDate.getTime(),
            };
        }
        if (viewData.isAddButtonSelected) {
            fields.callToAction = {
                buttonType: viewData.buttonType,
                link: viewData.buttonUrl,
            };
            if (viewData.buttonType === GoogleButtonType.CALL) {
                fields.callToAction.link = null;
            }
        }
        return fields;
    };

    const view = fromGoogleFieldsToView(googleFields);

    const saveView = (newViewData: GoogleFieldsView): void => {
        const newGoogleFields = fromViewToGoogleFields(newViewData);

        dispatch({ type: 'set-google-fields', payload: { value: newGoogleFields } });
        dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'GOOGLE_FIELDS' } });
    };
    const toggleCovidAlert = (): void => {
        const newView = { ...view };

        newView.isMakeAlertSelected = !view.isMakeAlertSelected;
        if (newView.isMakeAlertSelected) {
            newView.isMakeEventSelected = false;
        }
        saveView(newView);
    };
    const togglePostAnEvent = (): void => {
        const newView = { ...view };

        newView.isMakeEventSelected = !view.isMakeEventSelected;
        if (newView.isMakeEventSelected) {
            newView.isMakeAlertSelected = false;
        }
        saveView(newView);
    };
    const toggleAddButton = (): void => {
        const newView = { ...view };

        newView.isAddButtonSelected = !view.isAddButtonSelected;
        saveView(newView);
    };
    const saveEventTitle = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const newView = { ...view };

        newView.eventTitle = e.target.value;
        saveView(newView);
    };
    const saveEventStartDate = (date: Date): void => {
        if (date) {
            const newView = { ...view };

            newView.startDate = date;
            if (date.getTime() >= view.endDate.getTime()) {
                newView.endDate = new Date(date.getTime() + 1800000);
            }
            saveView(newView);
        }
    };
    const saveEventEndDate = (date: Date): void => {
        if (date) {
            const newView = { ...view };

            newView.endDate = date;
            saveView(newView);
        }
    };
    const saveButtonType = (buttonType: GoogleButtonType): void => {
        const newView = { ...view };

        newView.buttonType = buttonType;
        saveView(newView);
    };
    const saveButtonUrl = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const newView = { ...view };

        newView.buttonUrl = e.target.value;
        saveView(newView);
    };
    const selectedButton = GOOGLE_BUTTON_OPTIONS.find(
        (option) => option.value === view.buttonType,
    ) as GoogleButtonOption;

    return (
        <div className="r4e-social-google-fields-editor">
            <ErrorDisplay source="GOOGLE_PLACES" component="GOOGLE_FIELDS" publisherErrors={publisherErrors} />
            <div className="alert-type google-field">
                <Form.Group controlId="alertType">
                    <Form.Check
                        type="checkbox"
                        label={t('MAKE_COVID_ALERT_POST')}
                        checked={view.isMakeAlertSelected}
                        onChange={toggleCovidAlert}
                    />
                </Form.Group>
                {view.isMakeAlertSelected && (
                    <div className="alert-type-info">
                        <i className="icon-exclamation-triangle" />
                        <span>{t('COVID_ALERT_POST_DETAIL')}</span>
                    </div>
                )}
            </div>
            {!view.isMakeAlertSelected && (
                <div className="post-event google-field">
                    <Form.Group controlId="postEvent">
                        <Form.Check
                            type="checkbox"
                            label={t('MAKE_POST_AN_EVENT')}
                            checked={view.isMakeEventSelected}
                            onChange={togglePostAnEvent}
                        />
                    </Form.Group>
                    {view.isMakeEventSelected && (
                        <div className="post-event-options google-field-options">
                            <Form.Control
                                type="text"
                                placeholder={t('EVENT_TITLE')}
                                value={view.eventTitle}
                                onChange={saveEventTitle}
                                className="post-event-title"
                            />
                            <DatePickerWithLocale
                                selected={view.startDate}
                                onChange={saveEventStartDate}
                                showTimeSelect
                                minDate={Date.now()}
                                placeholderText={t('SELECT_START_DATE')}
                                dateFormat="Pp"
                                popperPlacement="top-start"
                                shouldCloseOnSelect={false}
                                className="event-start-date"
                            />
                            <div className="event-date-to">{t('TO')}</div>
                            <DatePickerWithLocale
                                selected={view.endDate}
                                onChange={saveEventEndDate}
                                showTimeSelect
                                minDate={view.startDate}
                                placeholderText={t('SELECT_END_DATE')}
                                dateFormat="Pp"
                                popperPlacement="top-start"
                                shouldCloseOnSelect={false}
                                className="event-end-date"
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="add-button google-field">
                <Form.Group controlId="addButton">
                    <Form.Check
                        type="checkbox"
                        label={t('ADD_A_BUTTON')}
                        checked={view.isAddButtonSelected}
                        onChange={toggleAddButton}
                    />
                </Form.Group>
                {view.isAddButtonSelected && (
                    <div className="add-button-options google-field-options">
                        <DropdownButton
                            id="button-types-dropdown"
                            title={t(selectedButton.label)}
                            className="button-types"
                            variant="default"
                        >
                            {GOOGLE_BUTTON_OPTIONS.map((option) => (
                                <Dropdown.Item
                                    key={option.value}
                                    active={selectedButton.value === option.value}
                                    onClick={(): void => saveButtonType(option.value)}
                                >
                                    {t(option.label)}
                                </Dropdown.Item>
                            ))}
                        </DropdownButton>
                        <Form.Control
                            type="text"
                            placeholder={
                                view.buttonType === GoogleButtonType.CALL
                                    ? t(selectedButton.placeholder)
                                    : selectedButton!.placeholder
                            }
                            value={view.buttonUrl}
                            onChange={saveButtonUrl}
                            disabled={view.buttonType === GoogleButtonType.CALL}
                            className="button-url"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
